import { FC, useMemo, useReducer } from 'react';

import { SquarePen } from 'lucide-react';
import { isEmpty, pick } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { TournamentGroupFormDialog } from '../tournamentGroupFormDialog/TournamentGroupFormDialog';

import css from './TournamentGroupsTableActionsColumn.module.css';

import { rewardsService } from 'v2/api/rewardsService';
import { tournamentGroupService } from 'v2/api/tournamentGroupService';
import { TournamentGroupForm, TournamentGroupModel } from 'v2/types/TournamentGroupModel';
import { handleHttpError, mapRewardsToFormRewards } from 'v2/utils';

interface TournamentGroupsTableActionsColumnProps {
  tournamentGroup: TournamentGroupModel;
  onRevalidate: VoidFunction;
}

export const TournamentGroupsTableActionsColumn: FC<TournamentGroupsTableActionsColumnProps> = ({
  tournamentGroup,
  onRevalidate,
}) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data } = useSWR(
    isOpen && !isEmpty(tournamentGroup.rewardIds) && ['getRewardsByIds', tournamentGroup.rewardIds],
    rewardsService.getRewardsByIds
  );

  const { trigger: triggerUpdate, isMutating: isMutatingUpdate } = useSWRMutation(
    ['updateTournamentGroup', tournamentGroup],
    tournamentGroupService.updateTournamentGroup,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The tournament group has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const initialValues = useMemo(() => {
    return {
      ...pick(tournamentGroup, ['name', 'autoHalfPrizesEnabled', 'rewardValue', 'images', 'colors']),
      rewards: isEmpty(tournamentGroup.rewardIds) ? [] : mapRewardsToFormRewards(data),
    };
  }, [data, tournamentGroup]);

  const handleSubmit = (values: TournamentGroupForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => resetForm());
  };

  return (
    <div className={css.tournamentGroupsTableActionsColumn}>
      <Tooltip title="Edit" placement="top">
        <IconButton onClick={setOpen} color="warning">
          <SquarePen width={22} height={22} />
        </IconButton>
      </Tooltip>

      <TournamentGroupFormDialog
        title="Edit Tournament Group"
        isOpen={isOpen}
        onClose={setOpen}
        initialValues={initialValues}
        isSubmitting={isMutatingUpdate}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
